// src/components/footer.jsx
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import BackToTopButton from './backToTop';
import { backendUrl } from './config';
import { UserContext } from './userContext';
import { dashboardPathForUser, isMarketingPath } from './public/publicRoutes';
import { BRAND, SERVICES } from './public/publicSiteData';
import { PUBLIC_NAVIGATION } from './public/publicSiteRegistry.mjs';
import './footerRedesign.css';

function ArrowIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Footer() {
  const { user } = useContext(UserContext);
  const location = useLocation();
  const responseRef = useRef(null);

  const [subscriptionEmail, setSubscriptionEmail] = useState('');
  const [subscriptionResponse, setSubscriptionResponse] = useState('');
  const [responseType, setResponseType] = useState('success');
  const [newsletterWebsite, setNewsletterWebsite] = useState('');
  const [loading, setLoading] = useState(false);

  const isPublicFooter = isMarketingPath(location.pathname);
  const dashboardPath = dashboardPathForUser(user);
  const year = new Date().getFullYear();

  useEffect(() => {
    if (subscriptionResponse) responseRef.current?.focus();
  }, [subscriptionResponse]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const email = subscriptionEmail.trim().toLowerCase();

    if (!EMAIL_PATTERN.test(email)) {
      setResponseType('error');
      setSubscriptionResponse('Please enter a valid email address.');
      return;
    }

    // Honeypot: silently accept obvious automated submissions.
    if (newsletterWebsite.trim()) {
      setResponseType('success');
      setSubscriptionResponse('Thank you. Your request was received.');
      setSubscriptionEmail('');
      setNewsletterWebsite('');
      return;
    }

    setLoading(true);
    setSubscriptionResponse('');

    const data = {
      from: email,
      name: 'TaskerSys website updates subscription',
      phone: 'Newsletter subscription',
      subject: 'TaskerSys website updates subscription',
      text: [
        `Newsletter subscription request: ${email}`,
        `Source page: ${location.pathname || '/'}`,
        'Consent: The visitor requested occasional TaskerSys and Copala Inc. product, platform, and field-service updates through the public website footer.',
      ].join('\n'),
    };

    try {
      const response = await fetch(`${backendUrl}/enquiryEmailSender`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(data),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(body.message || 'The subscription could not be submitted.');
      }

      setResponseType('success');
      setSubscriptionResponse(body.message || 'Thank you. Your email was received.');
      setSubscriptionEmail('');
      setNewsletterWebsite('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setResponseType('error');
      setSubscriptionResponse(
        error?.message || 'The subscription could not be submitted. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Keep authenticated application pages focused on product navigation.
  if (!isPublicFooter) {
    return (
      <footer className="ts-app-footer">
        <div className="ts-app-footer__inner">
          <div>
            <span className="ts-app-footer__brand">TaskerSys</span>
            <span> · Operated by Copala Inc. · © {year}</span>
          </div>
          <nav aria-label="Application footer">
            {user ? <Link to={dashboardPath}>Dashboard</Link> : <Link to="/signin">Sign in</Link>}
            {user ? <Link to="/profile">Profile</Link> : null}
            <a href={`mailto:${BRAND.email}`}>Help &amp; Support</a>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
            <BackToTopButton />
          </nav>
        </div>
      </footer>
    );
  }

  return (
    <footer className="ts-footer">
      <div className="ts-footer__container">
        <div className="ts-footer__intro">
          <div className="ts-footer__intro-copy">
            <p className="ts-footer__eyebrow">Property operations, connected</p>
            <h2>
              Keep property work connected <span>from request to closeout.</span>
            </h2>
            <p className="ts-footer__intro-description">
              Use the TaskerSys platform, add NYC field-service capacity for a defined scope,
              or connect both around the same assignment.
            </p>
          </div>
          <div className="ts-footer__intro-action">
            <Link className="ts-footer__primary-link" to="/contact">
              Discuss your next scope <ArrowIcon />
            </Link>
            <span>Share the property, scope, and timing. We will help identify the next step.</span>
          </div>
        </div>

        <div className="ts-footer__main">
          <section className="ts-footer__identity" aria-label="About TaskerSys">
            <Link className="ts-footer__logo" to="/" aria-label="TaskerSys home">
              Tasker<span>Sys</span>
            </Link>
            <p className="ts-footer__brand-description">
              Property operations software and optional NYC field services.
            </p>
            <p className="ts-footer__operator">Operated by <strong>{BRAND.legalName}</strong></p>
            <address className="ts-footer__address">{BRAND.address}</address>
          </section>

          <nav className="ts-footer__link-column" aria-label="Field services">
            <h3>Field services</h3>
            <ul>
              {SERVICES.map((service) => (
                <li key={service.slug}>
                  <Link to={`/services/${service.slug}`}>{service.shortTitle}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav className="ts-footer__link-column" aria-label="Explore TaskerSys">
            <h3>Explore</h3>
            <ul>
              {PUBLIC_NAVIGATION.filter(
                (item) => item.path !== '/services' && item.path !== '/contact'
              ).map((item) => (
                <li key={item.path}>
                  <Link to={item.path}>{item.label}</Link>
                </li>
              ))}
              {user ? (
                <>
                  <li><Link to={dashboardPath}>Open workspace</Link></li>
                  <li><Link to="/profile">Profile</Link></li>
                </>
              ) : (
                <>
                  <li><Link to="/signin">Sign in</Link></li>
                  <li><Link to="/signup">Create an account</Link></li>
                </>
              )}
            </ul>
          </nav>

          <section className="ts-footer__contact" aria-label="Contact TaskerSys">
            <h3>Get in touch</h3>
            <a className="ts-footer__phone" href={BRAND.phoneHref}>
              {BRAND.phoneDisplay}
            </a>
            <a className="ts-footer__email" href={`mailto:${BRAND.email}`}>
              {BRAND.email}
            </a>
            {BRAND.emailContext ? (
              <p className="ts-footer__email-context">{BRAND.emailContext}</p>
            ) : null}
            <Link className="ts-footer__contact-link" to="/contact">
              Contact TaskerSys <ArrowIcon />
            </Link>
          </section>
        </div>

        <section className="ts-footer__newsletter" aria-labelledby="ts-footer-updates-title">
          <div className="ts-footer__newsletter-heading">
            <p className="ts-footer__eyebrow">Occasional updates</p>
            <h3 id="ts-footer-updates-title">Product and field-service news, clearly labeled.</h3>
            <p>Receive occasional TaskerSys platform, public-resource, and service updates.</p>
          </div>

          <div className="ts-footer__newsletter-content">
            <form
              className="ts-footer__newsletter-form"
              onSubmit={handleSubmit}
              aria-busy={loading}
              noValidate
            >
              <label className="ts-footer__sr-only" htmlFor="taskersys-footer-newsletter">
                Email for TaskerSys updates
              </label>
              <input
                id="taskersys-footer-newsletter"
                name="email"
                type="email"
                value={subscriptionEmail}
                onChange={(event) => {
                  setSubscriptionEmail(event.target.value);
                  if (subscriptionResponse) setSubscriptionResponse('');
                }}
                placeholder="Your email address"
                autoComplete="email"
                inputMode="email"
                maxLength={180}
                required
                disabled={loading}
                aria-describedby="taskersys-footer-newsletter-privacy"
              />
              <label className="ts-footer__honeypot" aria-hidden="true">
                Website
                <input
                  type="text"
                  name="website"
                  value={newsletterWebsite}
                  onChange={(event) => setNewsletterWebsite(event.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </label>
              <button type="submit" disabled={loading}>
                {loading ? 'Sending…' : 'Get updates'} <ArrowIcon />
              </button>
            </form>
            <p id="taskersys-footer-newsletter-privacy" className="ts-footer__privacy-note">
              By subscribing, you request occasional emails from TaskerSys and Copala Inc.
              You may opt out at any time. See our{' '}
              <Link to="/privacy-policy">Privacy Policy</Link>.
            </p>
            {subscriptionResponse ? (
              <p
                ref={responseRef}
                tabIndex={-1}
                className={`ts-footer__response ts-footer__response--${responseType}`}
                role={responseType === 'error' ? 'alert' : 'status'}
                aria-live="polite"
              >
                {subscriptionResponse}
              </p>
            ) : null}
          </div>
        </section>

        <div className="ts-footer__bottom">
          <p>© {year} {BRAND.legalName}. All rights reserved.</p>
          <div className="ts-footer__bottom-right">
            <nav aria-label="Legal">
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/terms-of-service">Terms of Service</Link>
            </nav>
            <BackToTopButton />
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;



